import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  Panel,
  Handle,
  Position,
  ConnectionMode,
  BaseEdge,
  EdgeLabelRenderer,
  getStraightPath,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import { updateDoc } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import PropertyContext from "../contexts/PropertyContext";
import { propertyDoc } from "../utils/firestorePaths";
import {
  getBuildingsForRooms,
  getFloorsForRooms,
  getRoomFloor,
  inferFloorFromNumber,
  extractRoomNumber,
} from "../utils/floorInference";

// ── Tile sizing ──────────────────────────────────────────────────────────────
const DEFAULT_W = 130;
const DEFAULT_H = 90;
const MIN_DIM = 40;
const MAX_DIM = 600;

// ── Status colors ────────────────────────────────────────────────────────────
const STATUS_COLORS = {
  Up: "#2e7d32",
  Down: "#c62828",
  Occupied: "#1565c0",
  Blocked: "#616161",
  PM: "#ef6c00",
};
const statusColor = (status) => STATUS_COLORS[status] || "#9e9e9e";

// ── Edge styling ─────────────────────────────────────────────────────────────
const ADJACENCY_STROKE = "#90a4ae";
const CONJOINED_STROKE = "#7b1fa2";

const styleEdge = (edge, selectedNodeId, onDelete) => {
  const isConjoined = edge.connType === "conjoined";
  const touchesSelection =
    selectedNodeId && (edge.source === selectedNodeId || edge.target === selectedNodeId);
  return {
    id: edge.id,
    source: edge.source,
    target: edge.target,
    sourceHandle: edge.sourceSide || undefined,
    targetHandle: edge.targetSide || undefined,
    type: "roomEdge",
    animated: isConjoined,
    style: {
      stroke: isConjoined ? CONJOINED_STROKE : ADJACENCY_STROKE,
      strokeWidth: touchesSelection ? 4 : isConjoined ? 3 : 2,
    },
    data: { connType: edge.connType, onDelete },
  };
};

// ── Custom edge: line + "conjoined" tag + delete button ──────────────────────
const RoomEdge = ({ id, sourceX, sourceY, targetX, targetY, style, data }) => {
  const [path, labelX, labelY] = getStraightPath({ sourceX, sourceY, targetX, targetY });
  const isConjoined = data?.connType === "conjoined";
  return (
    <>
      <BaseEdge id={id} path={path} style={style} />
      <EdgeLabelRenderer>
        <div
          className="nodrag nopan"
          style={{
            position: "absolute",
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            pointerEvents: "all",
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          {isConjoined && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                color: CONJOINED_STROKE,
                background: "#f3e5f5",
                padding: "1px 5px",
                borderRadius: 4,
              }}
            >
              conjoined
            </span>
          )}
          <button
            title="Delete connection"
            onClick={(e) => {
              e.stopPropagation();
              data?.onDelete?.(id);
            }}
            style={{
              width: 18,
              height: 18,
              lineHeight: "16px",
              padding: 0,
              borderRadius: "50%",
              border: "1px solid #c62828",
              background: "#fff",
              color: "#c62828",
              cursor: "pointer",
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            ×
          </button>
        </div>
      </EdgeLabelRenderer>
    </>
  );
};

const edgeTypes = { roomEdge: RoomEdge };

// ── Custom room node ─────────────────────────────────────────────────────────
const HANDLE_STYLE = { width: 9, height: 9, background: "#78909c", border: "none" };

const RoomNode = ({ data, selected }) => {
  const color = statusColor(data.status);
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        textAlign: "center",
        borderRadius: 1,
        bgcolor: "#fff",
        border: "2px solid",
        borderColor: selected ? "primary.main" : color,
        boxShadow: selected ? 4 : 1,
      }}
    >
      {/* Loose connection mode: every handle can act as source or target */}
      <Handle type="source" position={Position.Top} id="t" style={HANDLE_STYLE} />
      <Handle type="source" position={Position.Right} id="r" style={HANDLE_STYLE} />
      <Handle type="source" position={Position.Bottom} id="b" style={HANDLE_STYLE} />
      <Handle type="source" position={Position.Left} id="l" style={HANDLE_STYLE} />
      <Typography sx={{ fontWeight: 700, fontSize: 14, lineHeight: 1.1 }}>
        {data.label}
      </Typography>
      <Box sx={{ mt: 0.5, display: "flex", alignItems: "center", gap: 0.5 }}>
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color }} />
        <Typography sx={{ fontSize: 10, color: "text.secondary" }}>
          {data.status || "—"}
        </Typography>
      </Box>
    </Box>
  );
};

const nodeTypes = { room: RoomNode };

// Default grid placement for rooms without a saved position.
const defaultPosition = (index) => {
  const COLS = 6;
  return { x: (index % COLS) * (DEFAULT_W + 30), y: Math.floor(index / COLS) * (DEFAULT_H + 30) };
};

const clampDim = (v) => Math.min(MAX_DIM, Math.max(MIN_DIM, Number(v) || MIN_DIM));

// Position of a room within its floor, used to match rooms across similar floors
// (e.g. 101 ↔ 201 ↔ 301 all share unit-index 1; "A-103" ↔ 3).
const floorUnitIndex = (displayName) => {
  const num = extractRoomNumber(displayName);
  if (num == null) return null;
  return num >= 100 ? num % 100 : num;
};

// Place neighbor Y flush against side `side` of node X (given their sizes).
const placeBeside = (xPos, xSize, ySize, side) => {
  switch (side) {
    case "r": return { x: xPos.x + xSize.w, y: xPos.y };
    case "l": return { x: xPos.x - ySize.w, y: xPos.y };
    case "b": return { x: xPos.x, y: xPos.y + xSize.h };
    case "t": return { x: xPos.x, y: xPos.y - ySize.h };
    default:  return { x: xPos.x + xSize.w, y: xPos.y };
  }
};

// ── Inner flow (needs ReactFlowProvider context) ─────────────────────────────
const PropertyMapFlow = () => {
  const { selectedProperty, selectedClient, rooms, propertyInformation } = useContext(PropertyContext);

  // Authoritative working layout (persisted on Save)
  const [positions, setPositions] = useState({}); // { [roomId]: {x,y} }
  const [sizes, setSizes] = useState({}); // { [roomId]: {w,h} } (override of default)
  const [allEdges, setAllEdges] = useState([]); // [{ id, source, target, sourceSide, targetSide, connType }]
  const [overrides, setOverrides] = useState({}); // { [roomId]: floor }
  const [dirty, setDirty] = useState(false);

  // View / UI state
  const [building, setBuilding] = useState("");
  const [floor, setFloor] = useState("");
  const [copySource, setCopySource] = useState(""); // "building|floor" to copy a layout from
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // react-flow live nodes (smooth dragging)
  const [nodes, setNodes] = useState([]);
  const nodesRef = useRef(nodes);
  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  const getSize = useCallback(
    (id) => sizes[id] || { w: DEFAULT_W, h: DEFAULT_H },
    [sizes]
  );

  // Load saved layout once propertyInformation arrives
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current || !propertyInformation) return;
    const map = propertyInformation.propertyMap;
    if (map) {
      setPositions(map.positions || {});
      setSizes(map.sizes || {});
      setAllEdges(
        (map.edges || []).map((e) => ({
          id: e.id || uuidv4(),
          source: e.source,
          target: e.target,
          sourceSide: e.sourceSide || null,
          targetSide: e.targetSide || null,
          // back-compat: old layouts used "suite"
          connType: e.type === "conjoined" || e.type === "suite" ? "conjoined" : "adjacency",
        }))
      );
      setOverrides(map.floorOverrides || {});
    }
    loadedRef.current = true;
  }, [propertyInformation]);

  const buildings = useMemo(() => getBuildingsForRooms(rooms), [rooms]);

  // Default building selection
  useEffect(() => {
    if (!building && buildings.length) setBuilding(buildings[0]);
  }, [buildings, building]);

  const buildingRooms = useMemo(
    () =>
      rooms.filter(
        (r) => String(r.Building != null ? r.Building : "1") === String(building)
      ),
    [rooms, building]
  );

  const floors = useMemo(
    () => getFloorsForRooms(buildingRooms, overrides),
    [buildingRooms, overrides]
  );

  // Default / clamp floor selection when building or floors change
  useEffect(() => {
    if (!floors.length) return;
    if (floor === "" || !floors.includes(Number(floor))) {
      setFloor(floors[0]);
    }
  }, [floors, floor]);

  const viewRooms = useMemo(
    () =>
      buildingRooms.filter((r) => getRoomFloor(r, overrides) === Number(floor)),
    [buildingRooms, overrides, floor]
  );

  const viewRoomIds = useMemo(() => new Set(viewRooms.map((r) => r.id)), [viewRooms]);

  // All (building, floor) combinations that exist — sources for "copy layout from".
  const floorOptions = useMemo(() => {
    const seen = new Map();
    rooms.forEach((r) => {
      const b = r.Building != null ? String(r.Building) : "1";
      const f = getRoomFloor(r, overrides);
      const key = `${b}|${f}`;
      if (!seen.has(key)) {
        seen.set(key, { key, building: b, floor: f, label: `Building ${b} · Floor ${f}` });
      }
    });
    return [...seen.values()].sort(
      (a, b) =>
        String(a.building).localeCompare(String(b.building), undefined, { numeric: true }) ||
        a.floor - b.floor
    );
  }, [rooms, overrides]);

  // Rebuild react-flow nodes when the view changes (NOT on every drag — positions
  // are authoritative and updated on drag stop, so live drags aren't disrupted).
  useEffect(() => {
    const built = viewRooms.map((room, idx) => {
      const size = getSize(room.id);
      return {
        id: room.id,
        type: "room",
        position: positions[room.id] || defaultPosition(idx),
        data: { label: room.displayName, status: room.status },
        width: size.w,
        height: size.h,
        style: { width: size.w, height: size.h },
      };
    });
    setNodes(built);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [building, floor, viewRooms.length]);

  // ── Connected-component helpers (for moving joined rooms together) ──
  const adjacency = useMemo(() => {
    const m = {};
    allEdges.forEach((e) => {
      (m[e.source] = m[e.source] || new Set()).add(e.target);
      (m[e.target] = m[e.target] || new Set()).add(e.source);
    });
    return m;
  }, [allEdges]);

  const getComponent = useCallback(
    (id) => {
      const seen = new Set([id]);
      const stack = [id];
      while (stack.length) {
        const cur = stack.pop();
        (adjacency[cur] || []).forEach((n) => {
          if (!seen.has(n)) {
            seen.add(n);
            stack.push(n);
          }
        });
      }
      return seen;
    },
    [adjacency]
  );

  // Re-snap an entire connected component, anchored at `anchorId`, walking edges
  // and placing each neighbor flush against the connecting side. `sizeOverride`
  // lets a just-resized room use its new dimensions immediately.
  const relayoutFrom = useCallback(
    (anchorId, sizeOverride) => {
      const sizeOf = (id) => (sizeOverride && sizeOverride[id]) || getSize(id);
      const posByNode = {};
      nodesRef.current.forEach((n) => {
        posByNode[n.id] = n.position;
      });
      const anchorPos = posByNode[anchorId];
      if (!anchorPos) return {};
      const result = { [anchorId]: anchorPos };
      const queue = [anchorId];
      while (queue.length) {
        const x = queue.shift();
        allEdges.forEach((e) => {
          let y = null;
          let side = null;
          if (e.source === x) {
            y = e.target;
            side = e.sourceSide;
          } else if (e.target === x) {
            y = e.source;
            side = e.targetSide;
          }
          if (y == null || result[y] !== undefined) return;
          result[y] = placeBeside(result[x], sizeOf(x), sizeOf(y), side);
          queue.push(y);
        });
      }
      return result;
    },
    [allEdges, getSize]
  );

  // Delete a connection (× button on the edge, or Delete key)
  const removeEdge = useCallback((edgeId) => {
    setAllEdges((eds) => eds.filter((e) => e.id !== edgeId));
    setDirty(true);
  }, []);

  const viewEdges = useMemo(
    () =>
      allEdges
        .filter((e) => viewRoomIds.has(e.source) && viewRoomIds.has(e.target))
        .map((e) => styleEdge(e, selectedNodeId, removeEdge)),
    [allEdges, viewRoomIds, selectedNodeId, removeEdge]
  );

  // ── Node interaction: drag moves the whole connected group together ──
  const handleNodesChange = useCallback(
    (changes) => {
      const posChanges = changes.filter((c) => c.type === "position" && c.position);
      if (posChanges.length) {
        const base = nodesRef.current;
        const byId = {};
        base.forEach((n) => {
          byId[n.id] = n;
        });
        const moves = {}; // id -> newPos
        posChanges.forEach((c) => {
          const cur = byId[c.id];
          if (!cur) return;
          const dx = c.position.x - cur.position.x;
          const dy = c.position.y - cur.position.y;
          moves[c.id] = c.position;
          getComponent(c.id).forEach((mid) => {
            if (mid === c.id || moves[mid] !== undefined) return;
            const m = byId[mid];
            if (m) moves[mid] = { x: m.position.x + dx, y: m.position.y + dy };
          });
        });
        setNodes((nds) => nds.map((n) => (moves[n.id] ? { ...n, position: moves[n.id] } : n)));
        if (posChanges.some((c) => c.dragging === false)) {
          setPositions((prev) => {
            const merged = { ...prev };
            Object.entries(moves).forEach(([id, p]) => {
              merged[id] = p;
            });
            return merged;
          });
          setDirty(true);
        }
      }

      const selChanges = changes.filter((c) => c.type === "select");
      if (selChanges.length) {
        setNodes((nds) =>
          nds.map((n) => {
            const ch = selChanges.find((c) => c.id === n.id);
            return ch ? { ...n, selected: ch.selected } : n;
          })
        );
        selChanges.forEach((ch) => {
          setSelectedNodeId((prev) => (ch.selected ? ch.id : prev === ch.id ? null : prev));
        });
      }
    },
    [getComponent]
  );

  // ── Connect: snap target flush against source on the connected edge ──
  const onConnect = useCallback(
    (conn) => {
      if (!conn.source || !conn.target || conn.source === conn.target) return;

      const srcSize = getSize(conn.source);
      const tgtSize = getSize(conn.target);
      const srcSide = conn.sourceHandle || "r";

      let snapPos = null;
      setNodes((nds) => {
        const src = nds.find((n) => n.id === conn.source);
        if (!src) return nds;
        snapPos = placeBeside(src.position, srcSize, tgtSize, srcSide);
        return nds.map((n) => (n.id === conn.target ? { ...n, position: snapPos } : n));
      });
      if (snapPos) setPositions((p) => ({ ...p, [conn.target]: snapPos }));

      setAllEdges((eds) => {
        const exists = eds.some(
          (e) =>
            (e.source === conn.source && e.target === conn.target) ||
            (e.source === conn.target && e.target === conn.source)
        );
        if (exists) return eds;
        return [
          ...eds,
          {
            id: uuidv4(),
            source: conn.source,
            target: conn.target,
            sourceSide: conn.sourceHandle || null,
            targetSide: conn.targetHandle || null,
            connType: "adjacency",
          },
        ];
      });
      setDirty(true);
    },
    [getSize]
  );

  // Right-click a connection to toggle adjacency ↔ conjoined
  const onEdgeContextMenu = useCallback((evt, edge) => {
    evt.preventDefault();
    setAllEdges((eds) =>
      eds.map((e) =>
        e.id === edge.id
          ? { ...e, connType: e.connType === "conjoined" ? "adjacency" : "conjoined" }
          : e
      )
    );
    setDirty(true);
  }, []);

  const onEdgesChange = useCallback((changes) => {
    const removed = changes.filter((c) => c.type === "remove").map((c) => c.id);
    if (removed.length) {
      setAllEdges((eds) => eds.filter((e) => !removed.includes(e.id)));
      setDirty(true);
    }
  }, []);

  const onPaneClick = useCallback(() => setSelectedNodeId(null), []);

  // ── Auto-arrange current view ──
  const handleAutoArrange = useCallback(() => {
    const sorted = [...viewRooms].sort(
      (a, b) =>
        (extractRoomNumber(a.displayName) || 0) - (extractRoomNumber(b.displayName) || 0)
    );
    const next = {};
    sorted.forEach((room, idx) => {
      next[room.id] = defaultPosition(idx);
    });
    setPositions((p) => ({ ...p, ...next }));
    setNodes((nds) => nds.map((n) => (next[n.id] ? { ...n, position: next[n.id] } : n)));
    setDirty(true);
  }, [viewRooms]);

  // ── Apply another floor's layout to the current floor ──
  // Matches rooms by unit-index (101 ↔ 201). Matched rooms take the source's
  // relative position, size, and connections. Rooms with no match are left
  // where they are (unless they have no saved position yet, in which case they
  // are parked in a column to the right so they're easy to find and place).
  const applyLayoutFrom = useCallback(
    (srcBuilding, srcFloor) => {
      const srcRooms = rooms.filter(
        (r) =>
          String(r.Building != null ? r.Building : "1") === String(srcBuilding) &&
          getRoomFloor(r, overrides) === Number(srcFloor)
      );
      if (!srcRooms.length) return;

      // Source positions (fall back to a grid for any unarranged source rooms)
      const srcSorted = [...srcRooms].sort(
        (a, b) =>
          (extractRoomNumber(a.displayName) || 0) - (extractRoomNumber(b.displayName) || 0)
      );
      const srcPos = {};
      srcSorted.forEach((r, i) => {
        srcPos[r.id] = positions[r.id] || defaultPosition(i);
      });
      const xs = Object.values(srcPos).map((p) => p.x);
      const ys = Object.values(srcPos).map((p) => p.y);
      const minX = Math.min(...xs);
      const minY = Math.min(...ys);

      // Template keyed by unit-index
      const template = {}; // unitIndex -> { rel:{x,y}, size }
      const idToUI = {};
      srcRooms.forEach((r) => {
        const ui = floorUnitIndex(r.displayName);
        idToUI[r.id] = ui;
        if (ui == null) return;
        template[ui] = {
          rel: { x: srcPos[r.id].x - minX, y: srcPos[r.id].y - minY },
          size: getSize(r.id),
        };
      });

      // Template connections (between two source rooms)
      const srcIds = new Set(srcRooms.map((r) => r.id));
      const templateEdges = allEdges
        .filter((e) => srcIds.has(e.source) && srcIds.has(e.target))
        .map((e) => ({
          a: idToUI[e.source],
          b: idToUI[e.target],
          sourceSide: e.sourceSide,
          targetSide: e.targetSide,
          connType: e.connType,
        }))
        .filter((te) => te.a != null && te.b != null);

      // Map current floor's rooms by unit-index
      const uiToTarget = {};
      viewRooms.forEach((r) => {
        const ui = floorUnitIndex(r.displayName);
        if (ui != null) uiToTarget[ui] = r;
      });

      const newPositions = { ...positions };
      const newSizes = { ...sizes };
      const matchedIds = new Set();

      Object.entries(template).forEach(([ui, t]) => {
        const tr = uiToTarget[ui];
        if (!tr) return;
        matchedIds.add(tr.id);
        newPositions[tr.id] = { x: t.rel.x, y: t.rel.y };
        newSizes[tr.id] = { ...t.size };
      });

      // Park unmatched rooms that have never been placed, off to the right.
      const tmplMaxX = Math.max(
        0,
        ...Object.values(template).map((t) => t.rel.x + t.size.w)
      );
      let park = 0;
      viewRooms.forEach((r) => {
        if (matchedIds.has(r.id) || positions[r.id]) return;
        newPositions[r.id] = { x: tmplMaxX + 60, y: park * (DEFAULT_H + 20) };
        park += 1;
      });

      // Rebuild connections among matched rooms from the template
      const newEdges = allEdges.filter(
        (e) => !(matchedIds.has(e.source) && matchedIds.has(e.target))
      );
      templateEdges.forEach((te) => {
        const ra = uiToTarget[te.a];
        const rb = uiToTarget[te.b];
        if (!ra || !rb) return;
        newEdges.push({
          id: uuidv4(),
          source: ra.id,
          target: rb.id,
          sourceSide: te.sourceSide,
          targetSide: te.targetSide,
          connType: te.connType,
        });
      });

      setPositions(newPositions);
      setSizes(newSizes);
      setAllEdges(newEdges);
      setNodes((nds) =>
        nds.map((n) => {
          if (newPositions[n.id]) {
            const s = newSizes[n.id] || getSize(n.id);
            return {
              ...n,
              position: newPositions[n.id],
              width: s.w,
              height: s.h,
              style: { width: s.w, height: s.h },
            };
          }
          return n;
        })
      );
      setDirty(true);
    },
    [rooms, overrides, positions, sizes, allEdges, viewRooms, getSize]
  );

  // ── Selected room: floor override + size ──
  const selectedRoom = useMemo(
    () => viewRooms.find((r) => r.id === selectedNodeId),
    [viewRooms, selectedNodeId]
  );

  const inferredFloor = selectedRoom
    ? inferFloorFromNumber(extractRoomNumber(selectedRoom.displayName))
    : null;

  const selectedSize = selectedRoom ? getSize(selectedRoom.id) : null;

  const setFloorOverride = (value) => {
    if (!selectedRoom) return;
    setOverrides((o) => ({ ...o, [selectedRoom.id]: Number(value) }));
    setDirty(true);
  };

  const resetFloorOverride = () => {
    if (!selectedRoom) return;
    setOverrides((o) => {
      const next = { ...o };
      delete next[selectedRoom.id];
      return next;
    });
    setDirty(true);
  };

  const setDimension = (dim, value) => {
    if (!selectedRoom) return;
    const id = selectedRoom.id;
    const current = getSize(id);
    const nextSize = {
      w: dim === "w" ? clampDim(value) : current.w,
      h: dim === "h" ? clampDim(value) : current.h,
    };
    setSizes((s) => ({ ...s, [id]: nextSize }));
    // Re-snap the connected group so neighbors stay flush against the new size.
    const moves = relayoutFrom(id, { [id]: nextSize });
    setNodes((nds) =>
      nds.map((n) => {
        if (n.id === id) {
          return {
            ...n,
            width: nextSize.w,
            height: nextSize.h,
            style: { width: nextSize.w, height: nextSize.h },
            position: moves[n.id] || n.position,
          };
        }
        return moves[n.id] ? { ...n, position: moves[n.id] } : n;
      })
    );
    setPositions((prev) => {
      const m = { ...prev };
      Object.entries(moves).forEach(([k, p]) => {
        m[k] = p;
      });
      return m;
    });
    setDirty(true);
  };

  // ── Save ──
  const handleSave = async () => {
    if (!selectedProperty) return;
    setSaving(true);
    try {
      // Fold latest live node positions into the authoritative map
      const mergedPositions = { ...positions };
      nodes.forEach((n) => {
        mergedPositions[n.id] = n.position;
      });
      await updateDoc(propertyDoc(selectedClient, selectedProperty), {
        propertyMap: {
          positions: mergedPositions,
          sizes,
          floorOverrides: overrides,
          edges: allEdges.map((e) => ({
            id: e.id,
            source: e.source,
            target: e.target,
            sourceSide: e.sourceSide || null,
            targetSide: e.targetSide || null,
            type: e.connType,
          })),
        },
      });
      setPositions(mergedPositions);
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      console.error("Error saving property map:", err);
    }
    setSaving(false);
  };

  if (!rooms.length) {
    return (
      <Typography color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
        No rooms found for this property yet.
      </Typography>
    );
  }

  return (
    <Box>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "center" }}
        sx={{ mb: 2 }}
      >
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Building</InputLabel>
          <Select
            label="Building"
            value={building}
            onChange={(e) => {
              setBuilding(e.target.value);
              setSelectedNodeId(null);
            }}
          >
            {buildings.map((b) => (
              <MenuItem key={b} value={b}>
                Building {b}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Floor</InputLabel>
          <Select
            label="Floor"
            value={floors.includes(Number(floor)) ? Number(floor) : ""}
            onChange={(e) => {
              setFloor(e.target.value);
              setSelectedNodeId(null);
            }}
          >
            {floors.map((f) => (
              <MenuItem key={f} value={f}>
                Floor {f}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ flexGrow: 1 }} />

        <FormControl size="small" sx={{ minWidth: 190 }}>
          <InputLabel>Copy layout from</InputLabel>
          <Select
            label="Copy layout from"
            value={copySource}
            onChange={(e) => setCopySource(e.target.value)}
          >
            <MenuItem value="">
              <em>Select a floor…</em>
            </MenuItem>
            {floorOptions.map((opt) => (
              <MenuItem key={opt.key} value={opt.key} disabled={opt.key === `${building}|${floor}`}>
                {opt.label}
                {opt.key === `${building}|${floor}` ? " (current)" : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AutoFixHighIcon />}
          onClick={() => {
            const [b, f] = copySource.split("|");
            applyLayoutFrom(b, f);
          }}
          disabled={!copySource}
        >
          Apply
        </Button>
        <Button
          size="small"
          variant="outlined"
          onClick={handleAutoArrange}
        >
          Grid arrange
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving || (!dirty && !saved)}
        >
          {saved ? "Saved!" : saving ? "Saving..." : dirty ? "Save Layout *" : "Save Layout"}
        </Button>
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        Drag from a room's edge dot to another room to connect — the room snaps flush
        against that edge, and joined rooms move together. Click the <strong>×</strong> on
        a connection to delete it, or <strong>right-click</strong> it to toggle{" "}
        <strong>adjacent ↔ conjoined</strong>. Select a room to adjust its size or floor.
      </Typography>

      <Box
        sx={{
          height: 560,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          position: "relative",
        }}
      >
        <ReactFlow
          nodes={nodes}
          edges={viewEdges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodesChange={handleNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onEdgeContextMenu={onEdgeContextMenu}
          onPaneClick={onPaneClick}
          connectionMode={ConnectionMode.Loose}
          deleteKeyCode={["Delete", "Backspace"]}
          snapToGrid
          snapGrid={[10, 10]}
          fitView
          fitViewOptions={{ padding: 0.2 }}
        >
          <Background gap={16} />
          <Controls />
          <MiniMap nodeColor={(n) => statusColor(n.data?.status)} pannable zoomable />

          {/* Legend */}
          <Panel position="top-left">
            <Box
              sx={{
                bgcolor: "rgba(255,255,255,0.9)",
                borderRadius: 1,
                p: 1,
                boxShadow: 1,
                display: "flex",
                gap: 2,
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box sx={{ width: 22, height: 3, bgcolor: ADJACENCY_STROKE }} />
                <Typography variant="caption">Adjacent</Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box sx={{ width: 22, height: 3, bgcolor: CONJOINED_STROKE }} />
                <Typography variant="caption">Conjoined</Typography>
              </Box>
            </Box>
          </Panel>

          {/* Selected room: size + floor override */}
          {selectedRoom && (
            <Panel position="top-right">
              <Box sx={{ bgcolor: "#fff", borderRadius: 1, p: 1.5, boxShadow: 3, minWidth: 220 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  {selectedRoom.displayName}
                </Typography>
                <Chip
                  size="small"
                  label={selectedRoom.status || "—"}
                  sx={{ my: 0.75, bgcolor: statusColor(selectedRoom.status), color: "#fff" }}
                />
                <Grid container spacing={1} sx={{ mt: 0.25 }}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Width"
                      value={selectedSize.w}
                      onChange={(e) => setDimension("w", e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      label="Height"
                      value={selectedSize.h}
                      onChange={(e) => setDimension("h", e.target.value)}
                    />
                  </Grid>
                </Grid>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  label="Floor"
                  value={getRoomFloor(selectedRoom, overrides)}
                  onChange={(e) => setFloorOverride(e.target.value)}
                  helperText={
                    overrides[selectedRoom.id] != null
                      ? `Overridden (inferred: ${inferredFloor})`
                      : `Inferred from room number`
                  }
                  sx={{ mt: 1 }}
                />
                {overrides[selectedRoom.id] != null && (
                  <Button size="small" onClick={resetFloorOverride} sx={{ mt: 0.5 }}>
                    Reset to inferred
                  </Button>
                )}
              </Box>
            </Panel>
          )}
        </ReactFlow>
      </Box>
    </Box>
  );
};

const PropertyMap = () => (
  <ReactFlowProvider>
    <PropertyMapFlow />
  </ReactFlowProvider>
);

export default PropertyMap;
